(function (root, factory) {
  'use strict';
  var api = factory(root);
  root.MEDeliveryAPI = Object.freeze({ quote: api.quote });
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : window, function (root) {
  'use strict';

  var ENDPOINT = '/api/2026-07/graphql.json';
  var MAX_RESPONSE_LENGTH = 1024 * 1024;
  var ZIP_REQUIRED = new Set(['US', 'CA', 'GB', 'AU', 'NZ', 'DE', 'FR', 'ES', 'IT', 'NL', 'CH', 'AT', 'BE', 'DK', 'FI', 'NO', 'SE', 'PT', 'PL', 'CZ', 'SK', 'HU', 'JP', 'IN', 'BR', 'MX', 'SG', 'KR', 'CN', 'ZA']);
  var CREATE_QUERY = 'mutation DeliveryQuoteCreate($input: CartInput!) { cartCreate(input: $input) { cart { id lines(first: 10) { nodes { id quantity merchandise { ... on ProductVariant { id } } } } } userErrors { code field message } warnings { code message } } }';
  var RATES_QUERY = 'query DeliveryQuoteRates($cartId: ID!) { cart(id: $cartId) { ... DeliveryQuoteGroups @defer } } fragment DeliveryQuoteGroups on Cart { deliveryGroups(first: 10, withCarrierRates: true) { nodes { id deliveryOptions { title description deliveryMethodType estimatedCost { amount currencyCode } } } pageInfo { hasNextPage endCursor } } }';

  function fail(code, message) {
    var error = new Error(message);
    error.name = 'DeliveryEstimateError';
    error.code = code;
    return error;
  }

  function invalidResponse() {
    return fail('INVALID_RESPONSE', 'We could not read the delivery estimate. Please try again.');
  }

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function cleanText(value, maxLength) {
    if (value === undefined || value === null) return '';
    if (typeof value !== 'string') throw fail('INVALID_INPUT', 'Please check the delivery address.');
    var text = value.trim();
    if (text.length > maxLength || /[\u0000-\u001f\u007f]/.test(text)) {
      throw fail('INVALID_INPUT', 'Please check the delivery address.');
    }
    return text;
  }

  function normalizeInput(input) {
    if (!isObject(input)) throw fail('INVALID_INPUT', 'Please select an item and enter your delivery destination.');
    var variant = typeof input.variantId === 'number' && Number.isSafeInteger(input.variantId)
      ? String(input.variantId) : input.variantId;
    if (typeof variant !== 'string' || !/^(?:gid:\/\/shopify\/ProductVariant\/)?[1-9]\d{0,24}$/.test(variant)) {
      throw fail('INVALID_INPUT', 'Please select an available product option.');
    }
    if (variant.indexOf('gid://') !== 0) variant = 'gid://shopify/ProductVariant/' + variant;

    var rawQuantity = input.quantity;
    if (typeof rawQuantity === 'string' && !/^\d+$/.test(rawQuantity)) rawQuantity = NaN;
    var quantity = typeof rawQuantity === 'number' || typeof rawQuantity === 'string' ? Number(rawQuantity) : NaN;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 9999) {
      throw fail('INVALID_INPUT', 'Please choose a whole-number quantity between 1 and 9,999.');
    }
    var countryCode = cleanText(input.countryCode, 2).toUpperCase();
    if (!/^[A-Z]{2}$/.test(countryCode)) throw fail('INVALID_INPUT', 'Please select a delivery country.');
    var address = input.address === undefined ? {} : input.address;
    if (!isObject(address)) throw fail('INVALID_INPUT', 'Please check the delivery address.');
    var zip = cleanText(address.zip, 32).toUpperCase();
    if (ZIP_REQUIRED.has(countryCode) && !zip) throw fail('INVALID_INPUT', 'Please enter a ZIP or postal code.');
    if (countryCode === 'US' && !/^\d{5}(?:-\d{4})?$/.test(zip)) {
      throw fail('INVALID_INPUT', 'Please enter a valid U.S. ZIP code.');
    }
    if (countryCode === 'CA') {
      var compactZip = zip.replace(/ /g, '');
      if (!/^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTVWXYZ]\d[ABCEGHJ-NPRSTVWXYZ]\d$/.test(compactZip)) {
        throw fail('INVALID_INPUT', 'Please enter a valid Canadian postal code.');
      }
      zip = compactZip.slice(0, 3) + ' ' + compactZip.slice(3);
    }
    var destination = { countryCode: countryCode };
    if (zip) destination.zip = zip;
    ['provinceCode', 'city', 'address1', 'address2'].forEach(function (field) {
      var value = cleanText(address[field], field === 'provinceCode' ? 32 : 255);
      if (value) destination[field] = value;
    });
    return { variantId: variant, quantity: quantity, countryCode: countryCode, address: destination };
  }

  function safeKey(key) {
    return typeof key === 'string' && key !== '__proto__' && key !== 'prototype' && key !== 'constructor';
  }

  function mergeData(target, source) {
    if (!isObject(source)) throw invalidResponse();
    Object.keys(source).forEach(function (key) {
      if (!safeKey(key)) throw invalidResponse();
      if (isObject(source[key])) {
        if (!isObject(target[key])) target[key] = {};
        mergeData(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    });
  }

  function applyIncremental(data, patch) {
    if (!isObject(patch) || !Array.isArray(patch.path) || !isObject(patch.data)) throw invalidResponse();
    // This query defers one fragment at cart; no array streaming is requested.
    if (patch.path.length !== 1 || patch.path[0] !== 'cart') throw invalidResponse();
    if (!isObject(data.cart)) throw invalidResponse();
    mergeData(data.cart, patch.data);
  }

  function readJSON(text) {
    try {
      var value = JSON.parse(text);
      if (!isObject(value)) throw invalidResponse();
      return value;
    } catch (error) {
      throw invalidResponse();
    }
  }

  function multipartPayloads(text, contentType) {
    var match = /(?:^|;)\s*boundary\s*=\s*(?:"([^"\r\n]+)"|([^;\s]+))/i.exec(contentType);
    if (!match) throw invalidResponse();
    var boundary = match[1] || match[2];
    if (boundary.length > 200 || /[\r\n]/.test(boundary)) throw invalidResponse();
    var escapedBoundary = boundary.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var marker = new RegExp('(?:^|\\r?\\n)--' + escapedBoundary + '(--)?[ \\t]*(?=\\r?\\n|$)', 'g');
    var markers = [];
    var found;
    while ((found = marker.exec(text))) markers.push({ start: found.index, end: marker.lastIndex, closing: !!found[1] });
    if (markers.length < 2 || markers[0].closing || !markers[markers.length - 1].closing) throw invalidResponse();
    var payloads = [];
    for (var index = 0; index < markers.length - 1; index += 1) {
      if (markers[index].closing) throw invalidResponse();
      var part = text.slice(markers[index].end, markers[index + 1].start).replace(/^\r?\n/, '');
      var divider = /\r?\n\r?\n/.exec(part);
      if (!divider) throw invalidResponse();
      var headers = part.slice(0, divider.index);
      if (!/^content-type:\s*application\/(?:json|graphql-response\+json)(?:\s*;[^\r\n]*)?\s*$/im.test(headers)) throw invalidResponse();
      payloads.push(readJSON(part.slice(divider.index + divider[0].length).trim()));
    }
    return payloads;
  }

  function parseGraphQLResponse(text, contentType) {
    if (typeof text !== 'string' || !text.trim() || text.length > MAX_RESPONSE_LENGTH) throw invalidResponse();
    contentType = typeof contentType === 'string' ? contentType : '';
    var multipart = /^multipart\/mixed(?:\s*;|$)/i.test(contentType);
    var payloads = multipart ? multipartPayloads(text, contentType) : [readJSON(text)];
    var data = {};
    var hasData = false;
    var sawContinuation = false;
    var completed = false;
    payloads.forEach(function (payload, index) {
      if (completed) throw invalidResponse();
      if (payload.errors !== undefined && (!Array.isArray(payload.errors) || payload.errors.length)) {
        throw fail('API_ERROR', 'Delivery estimates are temporarily unavailable. Please try again.');
      }
      if (payload.hasNext !== undefined && typeof payload.hasNext !== 'boolean') throw invalidResponse();
      if (isObject(payload.data)) {
        mergeData(data, payload.data);
        hasData = true;
      } else if (payload.data !== undefined && payload.data !== null) throw invalidResponse();
      if (payload.incremental !== undefined) {
        if (!Array.isArray(payload.incremental)) throw invalidResponse();
        payload.incremental.forEach(function (patch) {
          if (isObject(patch) && patch.errors !== undefined && (!Array.isArray(patch.errors) || patch.errors.length)) {
            throw fail('API_ERROR', 'The delivery provider could not return an estimate. Please try again.');
          }
          applyIncremental(data, patch);
          hasData = true;
        });
      }
      if (payload.hasNext === true) sawContinuation = true;
      if (payload.hasNext === false) completed = true;
      if (index < payloads.length - 1 && payload.hasNext === false) throw invalidResponse();
    });
    if (!hasData || ((multipart || sawContinuation) && !completed)) throw invalidResponse();
    return data;
  }

  function validateQuoteCart(data, input) {
    var payload = data && data.cartCreate;
    if (!isObject(payload) || !Array.isArray(payload.userErrors) || !Array.isArray(payload.warnings)) throw invalidResponse();
    if (payload.userErrors.length) {
      throw fail('API_ERROR', 'We could not estimate delivery for this selection. Check the item, quantity, and destination.');
    }
    if (payload.warnings.length) {
      throw fail('CART_CHANGED', 'The selected item or quantity changed. Please review your selection and try again.');
    }
    var cart = payload.cart;
    if (!isObject(cart) || typeof cart.id !== 'string' || !cart.id || cart.id.length > 8192) throw invalidResponse();
    var lines = cart.lines && cart.lines.nodes;
    if (!Array.isArray(lines) || lines.length !== 1 || !isObject(lines[0]) || lines[0].quantity !== input.quantity ||
        !isObject(lines[0].merchandise) || lines[0].merchandise.id !== input.variantId) {
      throw fail('CART_CHANGED', 'The selected item or quantity is unavailable for this estimate. Please review your selection.');
    }
    return cart.id;
  }

  function normalizeGroups(data) {
    var connection = data && data.cart && data.cart.deliveryGroups;
    if (!isObject(connection) || !Array.isArray(connection.nodes)) throw invalidResponse();
    if (connection.pageInfo && connection.pageInfo.hasNextPage) throw invalidResponse();
    if (!connection.nodes.length) {
      throw fail('RATE_UNAVAILABLE', 'An estimate is not available for this destination. Check your details or continue to checkout.');
    }
    return {
      groups: connection.nodes.map(function (group) {
        if (!isObject(group) || !Array.isArray(group.deliveryOptions)) throw invalidResponse();
        if (!group.deliveryOptions.length) {
          throw fail('RATE_UNAVAILABLE', 'An estimate is not available for this destination. Check your details or continue to checkout.');
        }
        return {
          options: group.deliveryOptions.map(function (option) {
            if (!isObject(option)) throw invalidResponse();
            var cost = option.estimatedCost;
            if (!isObject(cost) || typeof cost.amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(cost.amount) ||
                !Number.isFinite(Number(cost.amount)) || Number(cost.amount) > 1000000000 ||
                typeof cost.currencyCode !== 'string' || !/^[A-Z]{3}$/.test(cost.currencyCode) ||
                typeof option.deliveryMethodType !== 'string' || !/^[A-Z_]+$/.test(option.deliveryMethodType) ||
                (option.title !== null && option.title !== undefined && typeof option.title !== 'string') ||
                (option.description !== null && option.description !== undefined && typeof option.description !== 'string')) throw invalidResponse();
            return {
              title: option.title || 'Delivery',
              description: option.description || '',
              deliveryMethodType: option.deliveryMethodType,
              estimatedCost: { amount: cost.amount, currencyCode: cost.currencyCode }
            };
          })
        };
      })
    };
  }

  function throwIfAborted(signal) {
    if (signal && signal.aborted) {
      if (typeof root.DOMException === 'function') throw new root.DOMException('The request was cancelled.', 'AbortError');
      var error = new Error('The request was cancelled.');
      error.name = 'AbortError';
      throw error;
    }
  }

  function createClient(fetchImplementation) {
    async function request(query, variables, signal) {
      throwIfAborted(signal);
      var response;
      try {
        response = await fetchImplementation(ENDPOINT, {
          method: 'POST',
          credentials: 'omit',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'multipart/mixed; deferSpec=20220824, application/graphql-response+json, application/json'
          },
          body: JSON.stringify({ query: query, variables: variables }),
          signal: signal
        });
        if (!response.ok) throw fail('API_ERROR', 'Delivery estimates are temporarily unavailable. Please try again.');
        var text = await response.text();
        throwIfAborted(signal);
        return parseGraphQLResponse(text, response.headers.get('content-type'));
      } catch (error) {
        throwIfAborted(signal);
        if (error && (error.name === 'AbortError' || error.name === 'DeliveryEstimateError')) throw error;
        throw fail('API_ERROR', 'We could not connect to the delivery provider. Please try again.');
      }
    }

    return async function quote(input) {
      var normalized = normalizeInput(input);
      var signal = input.signal;
      var created = await request(CREATE_QUERY, {
        input: {
          lines: [{ merchandiseId: normalized.variantId, quantity: normalized.quantity }],
          buyerIdentity: { countryCode: normalized.countryCode },
          delivery: {
            addresses: [{
              selected: true,
              oneTimeUse: true,
              validationStrategy: 'COUNTRY_CODE_ONLY',
              address: { deliveryAddress: normalized.address }
            }]
          }
        }
      }, signal);
      var cartId = validateQuoteCart(created, normalized);
      var rates = await request(RATES_QUERY, { cartId: cartId }, signal);
      return normalizeGroups(rates);
    };
  }

  var quote = createClient(function (url, options) { return root.fetch(url, options); });
  return { quote: quote, createClient: createClient, parseGraphQLResponse: parseGraphQLResponse, normalizeInput: normalizeInput, validateQuoteCart: validateQuoteCart, normalizeGroups: normalizeGroups };
});
